/**
 * TourContext — app-level provider for the onboarding product tour.
 *
 * Rendered once above <Routes> so the tour survives page navigation. Fetches the
 * per-account tour status (so the prompt only auto-shows once across devices),
 * offers a dismissible "take a tour" prompt to new users, and exposes startTour()
 * so anything (e.g. the dashboard's "Take a tour" button) can launch it on demand.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useAuth } from './AuthContext';
import { UserData } from '../utils/tokenManager';
import ProductTour, { TourStep } from '../components/ProductTour';
import { getTourStatus, markTourCompleted } from '../services/tourService';
import { API_ENDPOINTS } from '../config/api';

// Internal step shape: a TourStep plus an optional access gate. Steps whose `requires`
// returns false (the account can't use that feature) are skipped — so the tour never
// navigates to a page the user will just get bounced off of.
type GatedStep = TourStep & { requires?: (u: UserData | null) => boolean };

// Access flags default to enabled; only an explicit `false` disables a feature.
const enabled = (v?: boolean) => v !== false;

const TOUR_STEPS: GatedStep[] = [
  // Cover the whole dashboard first, then walk the (accessible) feature pages, ending on Reports.
  { route: '/dashboard', selector: '[data-tour="welcome"]', title: 'Welcome to AutoAccess',
    body: "Here's a quick 60-second tour. Click Next to step through it, or Skip anytime — you can restart it later from the dashboard." },
  { route: '/dashboard', selector: '[data-tour="metrics"]', title: 'Your usage at a glance',
    body: 'Searches and reports for the current month show up here.' },
  { route: '/dashboard', selector: '[data-tour="activity"]', title: "Today's activity",
    body: "Your most recent lookups appear here, each with a link to open its report." },
  { route: '/dashboard', selector: '[data-tour="nav"]', title: 'Everything lives in this menu',
    body: "Your lookups, reports and account are all in this menu. Next we'll peek at the ones you have access to." },
  { route: '/nmvtis-record', selector: '[data-tour="nmvtis-search"]', title: 'NMVTIS vehicle history',
    body: 'Pull a full NMVTIS title-history report — brands, prior titles, odometer and lien history — by VIN.',
    requires: (u) => enabled(u?.canAccessNmvtis) },
  { sampleUrl: API_ENDPOINTS.VEHICLE_REPORTS.SAMPLE_PDF, title: 'Your NMVTIS report',
    body: "Here's a sample NMVTIS history report — title history across states, odometer readings and brand checks, in one branded PDF.",
    requires: (u) => enabled(u?.canAccessNmvtis) },
  { route: '/motor-vehicle-record', selector: '[data-tour="mvr-search"]', title: 'Run a state DMV record (MVR)',
    body: "Enter a VIN, pick a state and your permissible use, then click Search. (We won't run a real lookup during the tour.)",
    requires: (u) => enabled(u?.canAccessMotorVehicleRecord) },
  { sampleUrl: API_ENDPOINTS.STATE_DMV.SAMPLE_MVR_PDF, title: 'Your MVR report',
    body: "And here's a sample state DMV (MVR) report — title, owner, lienholder and odometer details, ready to view, download, or share.",
    requires: (u) => enabled(u?.canAccessMotorVehicleRecord) },
  { route: '/vin-decode', selector: '[data-tour="vin-decode"]', title: 'Decode any VIN',
    body: 'Instantly decode a VIN into year, make, model, specs and equipment — no charge.',
    requires: (u) => enabled(u?.canAccessVinDecoder) },
  { route: '/management', selector: '[data-tour="org-tabs"]', title: 'Manage your organization',
    body: "As an admin you run your team from here: Users (add/remove and set roles), Invitations (invite teammates), API Keys (programmatic access), and Settings (org policies like MFA and password rules).",
    requires: (u) => u?.role === 'ClientAdmin' },
  { route: '/record-inquiry-log', selector: '[data-tour="reports"]', title: "Reports & history — you're all set!",
    body: "Aggregated counts and downloadable detail across your inquiry history live here. That's the tour — everything's in the left menu whenever you need it." },
];

interface TourContextValue {
  startTour: () => void;
}

const TourContext = createContext<TourContextValue>({ startTour: () => {} });
export const useTour = () => useContext(TourContext);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [run, setRun] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [completed, setCompleted] = useState<boolean | null>(null);

  // Fetch the per-account tour status once authenticated.
  useEffect(() => {
    let active = true;
    if (isAuthenticated && user) {
      getTourStatus()
        .then((c) => { if (active) setCompleted(c); })
        .catch(() => { if (active) setCompleted(true); }); // fail closed — don't nag on error
    } else {
      setCompleted(null);
      setShowPrompt(false);
      setRun(false);
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // Offer the prompt shortly after login if they've never done the tour.
  useEffect(() => {
    if (completed === false && !run) {
      const t = setTimeout(() => setShowPrompt(true), 900);
      return () => clearTimeout(t);
    }
  }, [completed, run]);

  const markDone = useCallback(() => {
    setCompleted(true);
    markTourCompleted().catch(() => {});
  }, []);

  // Only show steps for features this account can actually use.
  const steps = useMemo<TourStep[]>(
    () => TOUR_STEPS.filter((s) => !s.requires || s.requires(user)).map(({ requires, ...step }) => step),
    [user],
  );

  const startTour = useCallback(() => { setShowPrompt(false); setRun(true); }, []);
  const skipPrompt = () => { setShowPrompt(false); markDone(); };
  const closeTour = () => { setRun(false); markDone(); };

  return (
    <TourContext.Provider value={{ startTour }}>
      {children}

      {showPrompt && (
        <div className="fixed bottom-6 right-6 z-[9998] w-80 bg-white rounded-2xl shadow-2xl border border-black/[0.06] p-5 animate-fade-in-up">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#c41e3a]/10 to-[#c41e3a]/5 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-[#c41e3a]" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#0f172a]">New here?</h3>
              <p className="text-sm text-[#475569] mt-0.5">Take a quick tour of AutoAccess — it only takes a minute.</p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button onClick={skipPrompt} className="text-sm text-[#94a3b8] hover:text-[#475569] font-medium px-2 py-1.5">Skip</button>
            <button onClick={startTour} className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#c41e3a] to-[#a01830] text-white text-sm font-semibold hover:from-[#a01830] hover:to-[#8a1428] transition-all">Start tour</button>
          </div>
        </div>
      )}

      <ProductTour steps={steps} run={run} onClose={closeTour} />
    </TourContext.Provider>
  );
};
